/**
 * useTargetLifecycleStream — timeline of the SELECTED TARGET's combat
 * actions, parallel to useCastLifecycleStream (which monitors the
 * LOCAL player's casts).
 *
 * Today's source: server `mobAttackDiagnostic` broadcasts emitted from
 * `MobAISystem.updateAttack` on every attack-fire. Each event carries
 * the gate snapshot (distance, effectiveRange, leashDist/Max, damage,
 * interval) so DJ can answer "should this attack have hit me?" by
 * reading the values, not staring at the sprite.
 *
 * Filtering: events are accepted only if `mobId === activeTargetId`.
 * `activeTargetId` is polled from TargetingSystem.getTarget() at 200ms
 * — the same cadence the on-screen target frame uses. Switching
 * targets clears prior groups so the panel only shows the current
 * target's history.
 *
 * Future:
 *   - State-transition events (idle→aggro, leash, etc.)
 *   - Mob ability casts (when mobs cast — bosses already do)
 *   - Player → mob auto-attacks (mirror direction)
 *
 * Plan: docs/plans/ability-qa-loop.md (target-lifecycle slice)
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { EventBus } from '../../../shared/EventBus';
import { ServiceRegistry } from '../../core/ServiceRegistry';

export type TargetEventCategory = 'attack' | 'state' | 'ability' | 'bump';

export interface TargetEvent {
  time: number;
  category: TargetEventCategory;
  /** e.g. "attack-fire" or "state:aggro→chase". */
  label: string;
  /** Free-form display detail — pre-formatted by the source. */
  detail: string;
  /** Optional structured fields when present (panel renders dedicated rows). */
  distance?: number;
  range?: number;
  damage?: number;
}

export interface TargetGroup {
  /** Aggregation key — currently the mob's id; one group per mob. */
  groupId: string;
  /** Display label for the group header (mob name). */
  mobName: string;
  startTime: number;
  endTime: number;
  events: TargetEvent[];
}

export interface UseTargetLifecycleStreamOptions {
  maxEventsPerGroup?: number;
  paused?: boolean;
}

export interface UseTargetLifecycleStreamResult {
  activeTargetId: string | null;
  group: TargetGroup | null;
  paused: boolean;
  pause: () => void;
  resume: () => void;
  clear: () => void;
}

const DEFAULT_MAX_EVENTS = 64;
const TARGET_POLL_MS = 200;

export function useTargetLifecycleStream(
  options: UseTargetLifecycleStreamOptions = {},
): UseTargetLifecycleStreamResult {
  const maxEvents = options.maxEventsPerGroup ?? DEFAULT_MAX_EVENTS;

  const [paused, setPaused] = useState<boolean>(options.paused ?? false);
  const [activeTargetId, setActiveTargetId] = useState<string | null>(null);
  const [group, setGroup] = useState<TargetGroup | null>(null);

  const pausedRef = useRef(paused);
  pausedRef.current = paused;
  const activeIdRef = useRef<string | null>(null);
  activeIdRef.current = activeTargetId;
  const groupRef = useRef<TargetGroup | null>(null);
  groupRef.current = group;

  // ── Target polling — flip activeTargetId on lock change ───────
  useEffect(() => {
    const poll = () => {
      const ts = ServiceRegistry.getInstance().get('TargetingSystem', { optional: true });
      const id = ts?.getTarget?.() ?? null;
      if (id !== activeIdRef.current) {
        setActiveTargetId(id);
        // Reset group on target change so the panel only shows the
        // current target's events. Without this, switching from pig A
        // to pig B would interleave A's prior events with B's new ones.
        setGroup(null);
      }
    };
    poll();
    const interval = setInterval(poll, TARGET_POLL_MS);
    return () => clearInterval(interval);
  }, []);

  const ingest = useCallback(
    (mobId: string, mobName: string, event: TargetEvent) => {
      if (pausedRef.current) return;
      if (mobId !== activeIdRef.current) return;

      const prev = groupRef.current;
      const next: TargetGroup = prev && prev.groupId === mobId
        ? {
            ...prev,
            mobName,
            endTime: event.time,
            events: [...prev.events.slice(-(maxEvents - 1)), event],
          }
        : {
            groupId: mobId,
            mobName,
            startTime: event.time,
            endTime: event.time,
            events: [event],
          };
      setGroup(next);
    },
    [maxEvents],
  );

  // ── Server `mobAttackDiagnostic` subscription ─────────────────
  useEffect(() => {
    const off = EventBus.getInstance().on('net:message:mobAttackDiagnostic', (msg: any) => {
      const mobId = msg?.mobId;
      const mobName = msg?.mobName ?? mobId ?? '?';
      if (!mobId || typeof mobId !== 'string') return;

      const distance = typeof msg?.distance === 'number' ? msg.distance : undefined;
      const range = typeof msg?.effectiveRange === 'number' ? msg.effectiveRange : undefined;
      const damage = typeof msg?.damage === 'number' ? msg.damage : undefined;
      const targetSid = msg?.targetSessionId ?? '?';
      // L3.3 + 2026-05-02 — server reports `blocked: 'los' | 'range'`.
      //   - 'los'   → carries `blockerType` ('normal' / 'cover' /
      //               'player' / 'corpse') for the first occluder.
      //   - 'range' → end-of-pipeline range re-check failed (target
      //               kited out between state-entry and tick-fire).
      const blockedReason: 'los' | 'range' | undefined =
        msg?.blocked === 'los' || msg?.blocked === 'range' ? msg.blocked : undefined;
      const blockerType: string | undefined =
        typeof msg?.blockerType === 'string' ? msg.blockerType : undefined;
      const blockedDetail = blockedReason === 'range'
        ? 'range'
        : (blockerType ?? 'los');

      const baseDetail = distance != null && range != null
        ? `${distance}px / ${range}px range  ·  ${damage ?? '?'} dmg → ${targetSid}`
        : JSON.stringify(msg);

      const detail = blockedReason
        ? `🚫 BLOCKED (${blockedDetail})  ·  ${baseDetail}`
        : baseDetail;

      ingest(mobId, mobName, {
        time: msg?.timestamp ?? Date.now(),
        category: 'attack',
        label: blockedReason ? `blocked-${blockedDetail}` : 'attack-fire',
        detail,
        distance,
        range,
        damage,
      });
    });

    // ── Server `mobBumpDiagnostic` subscription ─────────────────
    // Fires when a mob is shoved out of a player AABB overlap (bounded
    // by BUMP_COOLDOWN_MS — ~12Hz max per mob). Mob is keyed by
    // `mobId` so it filters into the locked-target's row group like
    // attack-fire events.
    const offBump = EventBus.getInstance().on('net:message:mobBumpDiagnostic', (msg: any) => {
      const mobId = msg?.mobId;
      const mobName = msg?.mobName ?? mobId ?? '?';
      if (!mobId || typeof mobId !== 'string') return;

      const pushPx: number = typeof msg?.pushPx === 'number' ? msg.pushPx : 0;
      const bumpedAgainst: string = msg?.bumpedAgainstSid ?? '?';
      const dir = pushPx < 0 ? '◀ left' : '▶ right';
      const detail = `pushed ${dir} ${Math.abs(pushPx)}px (vs ${bumpedAgainst})`;

      ingest(mobId, mobName, {
        time: msg?.timestamp ?? Date.now(),
        category: 'bump',
        label: 'bump-resolved',
        detail,
      });
    });

    return () => { off(); offBump(); };
  }, [ingest]);

  return {
    activeTargetId,
    group,
    paused,
    pause: () => setPaused(true),
    resume: () => setPaused(false),
    clear: () => setGroup(null),
  };
}

/**
 * Serialize the active group to a clipboard-friendly string. Mirrors
 * AbilityLifecycleMonitor's serializeGroupsForCopy shape.
 */
export function serializeTargetGroupForCopy(group: TargetGroup | null): string {
  if (!group) return '(no target events)';
  const head = `Target: ${group.mobName} (${group.groupId})  ${group.events.length} event${group.events.length === 1 ? '' : 's'}`;
  const lines = group.events.map((e) => {
    const dt = e.time - group.startTime;
    return `  +${dt}ms  ${e.category}:${e.label} — ${e.detail}`;
  });
  return [head, ...lines].join('\n');
}
